/** Back office → Contracts (C6): every contract and amendment, filters, new contract. */

import { type ContractStatusEnum, useContractsItemsList } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Avatar, Badge, Button, EmptyState, GlassCard, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { FilePlus2, FileSignature } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { useCurrentRole } from "../lib/settings";
import { NewContractSheet } from "./contracts/NewContractSheet";
import { STATUS_TONE, dateOnly } from "./contracts/shared";

const STATUSES: ContractStatusEnum[] = ["draft", "ready", "sent", "signed", "cancelled"];

export function ContractsPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"" | ContractStatusEnum>("");
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const role = useCurrentRole("contracts");
  const canWrite = role === "owner" || role === "manager" || role === "advisor";
  const needle = query.trim();
  const contracts = useContractsItemsList({ page, ...(status ? { status } : {}), ...(needle ? { search: needle } : {}) });
  const data = contracts.data?.status === 200 ? contracts.data.data : undefined;
  const rows = data?.results ?? [];
  const counts = STATUSES.map((s) => ({ status: s, count: (data?.results ?? []).filter((c) => c.status === s).length }));

  return (
    <Page
      title={t("contracts.title")}
      description={t("contracts.intro")}
      actions={
        canWrite ? (
          <Button icon={<FilePlus2 size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
            {t("contracts.new.open")}
          </Button>
        ) : null
      }
    >
      <div className="kpis">
        {counts
          .filter((c) => c.status !== "cancelled")
          .map((c) => (
            <GlassCard key={c.status} as="div" className="kpi">
              <button type="button" className="kpi__link contract-kpi" onClick={() => setStatus(status === c.status ? "" : c.status)} aria-pressed={status === c.status}>
                <span className="kpi__label">{t(`contracts.status.${c.status}`)}</span>
                <span className="kpi__value">{c.count}</span>
              </button>
            </GlassCard>
          ))}
      </div>
      <GlassCard className="stack">
        <div className="list-toolbar">
          <TextField
            label={t("contracts.list.search")}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SelectField
            label={t("contracts.list.status")}
            value={status}
            options={[
              { value: "", label: t("contracts.list.allStatuses") },
              ...STATUSES.map((s) => ({ value: s, label: t(`contracts.status.${s}`) })),
            ]}
            onChange={(e) => setStatus(e.target.value as "" | ContractStatusEnum)}
          />
        </div>
        {contracts.isPending ? <Skeleton style={{ height: 160 }} /> : null}
        {contracts.isError ? (
          <p role="alert" className="ko">
            {t("common.error")}
          </p>
        ) : null}
        {data && rows.length === 0 ? (
          <EmptyState
            icon={<FileSignature size={22} strokeWidth={1.75} />}
            title={data.results.length ? t("contracts.list.noMatch") : t("contracts.list.empty")}
          />
        ) : null}
        {rows.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("contracts.list.number")}</th>
                  <th>{t("contracts.list.client")}</th>
                  <th>{t("contracts.list.package")}</th>
                  <th className="nowrap">{t("contracts.list.total")}</th>
                  <th>{t("contracts.list.status")}</th>
                  <th>{t("contracts.list.created")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/contracts/${c.id}`} className="link">
                        {c.number || t("contracts.draftNumber")}
                      </Link>
                      {c.kind === "amendment" ? (
                        <>
                          {" "}
                          <Badge>{t("contracts.kind.amendment")}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <span className="lead-cell">
                        <Avatar name={c.contact_name} />
                        {c.contact_name}
                      </span>
                    </td>
                    <td>
                      <span className="code-chip">{c.package_code}</span>
                    </td>
                    <td className="nowrap ao-num">{formatXaf(c.total_xaf)}</td>
                    <td>
                      <Badge dot tone={STATUS_TONE[c.status]}>{t(`contracts.status.${c.status}`)}</Badge>
                    </td>
                    <td className="muted">{dateOnly(c.created_at, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {data && (data.next || data.previous) ? (
          <div className="row between">
            <Button variant="secondary" disabled={!data.previous} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              {t("contracts.list.previous")}
            </Button>
            <span className="muted">{t("contracts.list.page", { page, total: data.count })}</span>
            <Button variant="secondary" disabled={!data.next} onClick={() => setPage((p) => p + 1)}>
              {t("contracts.list.next")}
            </Button>
          </div>
        ) : null}
      </GlassCard>
      {creating ? <NewContractSheet open onClose={() => setCreating(false)} /> : null}
    </Page>
  );
}
